import {
  REVIEW_GROUP_BY_OPTIONS,
  type ReviewGroupBy,
} from './purchasingReviewTableConfig'

const GROUP_CYCLE: ReviewGroupBy[] = REVIEW_GROUP_BY_OPTIONS.map((option) => option.value)

export type PurchasingReviewKeyboardContext = {
  showFilters: boolean
  showSort: boolean
  showColumns: boolean
  showRowModal: boolean
  showCreateProduct: boolean
  showPackEditor: boolean
  showBulkEdit: boolean
  showShortcutsModal: boolean
  groupBy: ReviewGroupBy
  hasSelection: boolean
  modalOpen: boolean
}

export type PurchasingReviewKeyboardActions = {
  setShowFilters: (value: boolean | ((prev: boolean) => boolean)) => void
  setShowSort: (value: boolean | ((prev: boolean) => boolean)) => void
  setShowColumns: (value: boolean | ((prev: boolean) => boolean)) => void
  setShowBulkEdit: (value: boolean) => void
  setShowShortcutsModal: (value: boolean) => void
  setGroupBy: (value: ReviewGroupBy) => void
  clearSelection: () => void
  focusSearch: () => void
  closeRowModal: () => void
  closeCreateProduct: () => void
  closePackEditor: () => void
  moveCursor: (direction: 'up' | 'down') => void
  toggleCursorSelection: () => void
  openCursorRow: () => void
  approveCursorOrSelection: () => void
  linkCursorOrSelection: () => void
  openProductPicker: () => void
  openBulkEdit: () => void
  modalPrevRow: () => void
  modalNextRow: () => void
}

export type PurchasingReviewShortcut = {
  key: string
  description: string
  group: 'Navigation' | 'Selection & actions' | 'Row modal'
}

/** Single source of truth for Review keyboard bindings (modal + docs). */
export const PURCHASING_REVIEW_KEYMAP: PurchasingReviewShortcut[] = [
  { group: 'Navigation', key: '/', description: 'Focus search' },
  { group: 'Navigation', key: '↑ / k', description: 'Move cursor up' },
  { group: 'Navigation', key: '↓ / j', description: 'Move cursor down' },
  { group: 'Navigation', key: 'Enter / o', description: 'Open row modal' },
  { group: 'Navigation', key: 'g', description: 'Cycle group-by' },
  { group: 'Navigation', key: '?', description: 'Show keyboard shortcuts' },
  { group: 'Selection & actions', key: 'x', description: 'Toggle selection on cursor row' },
  {
    group: 'Selection & actions',
    key: 'Shift + Click',
    description: 'Select range of visible rows',
  },
  {
    group: 'Selection & actions',
    key: 'a',
    description: 'Approve cursor or selection (match + lock in)',
  },
  { group: 'Selection & actions', key: 'p', description: 'Open product picker for cursor row' },
  {
    group: 'Selection & actions',
    key: 'l',
    description: 'Link cursor or selection (confirm product link)',
  },
  {
    group: 'Selection & actions',
    key: 'e',
    description: 'Open bulk edit when rows are selected',
  },
  {
    group: 'Selection & actions',
    key: 'Esc',
    description: 'Close modals, drawers, or clear selection',
  },
  { group: 'Row modal', key: 'j / ↓', description: 'Next row (when modal open)' },
  { group: 'Row modal', key: 'k / ↑', description: 'Previous row (when modal open)' },
]

export const SHORTCUT_GROUPS: Array<{
  title: PurchasingReviewShortcut['group']
  shortcuts: Array<{ key: string; description: string }>
}> = (['Navigation', 'Selection & actions', 'Row modal'] as const).map((title) => ({
  title,
  shortcuts: PURCHASING_REVIEW_KEYMAP.filter((entry) => entry.group === title).map(
    ({ key, description }) => ({ key, description }),
  ),
}))

export function nextCursorIndex(
  current: number | null,
  count: number,
  direction: 'up' | 'down',
): number | null {
  if (count <= 0) return null
  if (direction === 'down') {
    if (current === null) return 0
    if (current >= count - 1) return current
    return current + 1
  }
  if (current === null) return count - 1
  if (current <= 0) return current
  return current - 1
}

function isTextFieldTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== 'object' || !('tagName' in target)) return false
  const tag = String((target as { tagName?: string }).tagName ?? '').toUpperCase()
  const editable =
    'isContentEditable' in target &&
    Boolean((target as { isContentEditable?: boolean }).isContentEditable)
  if (editable) return true
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') {
    const type = String((target as HTMLInputElement).type || 'text').toLowerCase()
    if (type === 'checkbox' || type === 'radio' || type === 'button' || type === 'submit') {
      return false
    }
    return true
  }
  return false
}

export function handlePurchasingReviewKeyDown(
  event: KeyboardEvent,
  context: PurchasingReviewKeyboardContext,
  actions: PurchasingReviewKeyboardActions,
): boolean {
  const inTextField = isTextFieldTarget(event.target)

  if (inTextField && event.key !== 'Escape') {
    return false
  }

  if (
    (context.showBulkEdit || context.showCreateProduct || context.showPackEditor) &&
    event.key !== 'Escape'
  ) {
    return false
  }

  if (event.key === '?' && !event.metaKey && !event.ctrlKey && !event.altKey) {
    event.preventDefault()
    actions.setShowShortcutsModal(true)
    return true
  }

  if (context.showShortcutsModal && event.key !== 'Escape') {
    return false
  }

  if (context.showRowModal) {
    if (event.key === 'j' || event.key === 'ArrowDown') {
      event.preventDefault()
      actions.modalNextRow()
      return true
    }
    if (event.key === 'k' || event.key === 'ArrowUp') {
      event.preventDefault()
      actions.modalPrevRow()
      return true
    }
  } else if (!inTextField) {
    if (event.key === 'j' || event.key === 'ArrowDown') {
      event.preventDefault()
      actions.moveCursor('down')
      return true
    }
    if (event.key === 'k' || event.key === 'ArrowUp') {
      event.preventDefault()
      actions.moveCursor('up')
      return true
    }
  }

  switch (event.key) {
    case '/':
      event.preventDefault()
      actions.focusSearch()
      return true
    case 'g':
      if (inTextField) return false
      event.preventDefault()
      {
        const currentIdx = GROUP_CYCLE.indexOf(context.groupBy)
        const nextIdx = (currentIdx + 1) % GROUP_CYCLE.length
        actions.setGroupBy(GROUP_CYCLE[nextIdx]!)
      }
      return true
    case 'Enter':
    case 'o':
      if (inTextField || context.showRowModal) return false
      event.preventDefault()
      actions.openCursorRow()
      return true
    case 'a':
      if (inTextField) return false
      event.preventDefault()
      actions.approveCursorOrSelection()
      return true
    case 'p':
      if (inTextField) return false
      event.preventDefault()
      actions.openProductPicker()
      return true
    case 'l':
      if (inTextField) return false
      event.preventDefault()
      actions.linkCursorOrSelection()
      return true
    case 'e':
      if (inTextField) return false
      event.preventDefault()
      actions.openBulkEdit()
      return true
    case 'x':
      if (inTextField) return false
      event.preventDefault()
      actions.toggleCursorSelection()
      return true
    case 'Escape':
      event.preventDefault()
      if (context.showShortcutsModal) {
        actions.setShowShortcutsModal(false)
        return true
      }
      if (context.showRowModal) {
        actions.closeRowModal()
        return true
      }
      if (context.showCreateProduct) {
        actions.closeCreateProduct()
        return true
      }
      if (context.showPackEditor) {
        actions.closePackEditor()
        return true
      }
      if (context.showBulkEdit) {
        actions.setShowBulkEdit(false)
        return true
      }
      if (context.showFilters) {
        actions.setShowFilters(false)
        return true
      }
      if (context.showSort) {
        actions.setShowSort(false)
        return true
      }
      if (context.showColumns) {
        actions.setShowColumns(false)
        return true
      }
      if (context.hasSelection) {
        actions.clearSelection()
      }
      return true
    default:
      return false
  }
}
